import React, { useEffect, useState } from 'react';
import Icon from '../../components/Icon/Icon';
import { ActiveCallView } from '../../hooks/useWhatsAppCall';
import { playCentreIncomingSound, startRingback, stopRingback, unlockCentreNotifSound } from '../../utils/centreNotifSound';
import { canRouteCallAudio, setCallAudio, setCallSpeaker } from '../../utils/callAudio';
import './CentreCallOverlay.scss';

interface CentreCallOverlayProps {
  view: ActiveCallView;
  muted: boolean;
  onAccept: () => void;
  onReject: () => void;
  onHangup: () => void;
  onToggleMute: () => void;
  onToggleHold?: () => void;
}

const formatClock = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const initials = (name: string) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('');
};

const maskPhone = (phone: string) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 6) return phone;
  return `+${digits.slice(0, 2)} •• ${digits.slice(-4)}`;
};

const CentreCallOverlay: React.FC<CentreCallOverlayProps> = ({
  view,
  muted,
  onAccept,
  onReject,
  onHangup,
  onToggleMute,
  onToggleHold
}) => {
  const [elapsed, setElapsed] = useState(0);
  const [full, setFull] = useState(true);
  const [speaker, setSpeaker] = useState(false);
  const routeAudio = canRouteCallAudio();
  const busy = view.phase === 'connecting';
  const live = view.phase === 'active' || view.phase === 'outgoing' || view.phase === 'connecting';
  const phoneLabel = view.phone && view.phone !== view.name ? maskPhone(view.phone) : null;

  useEffect(() => {
    if (view.phase !== 'active') {
      setElapsed(0);
      return undefined;
    }
    const started = Date.now();
    const timer = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [view.phase, view.id]);

  useEffect(() => {
    if (view.phase !== 'incoming') return undefined;
    unlockCentreNotifSound();
    playCentreIncomingSound('alert', view.id);
    const timer = window.setInterval(() => {
      playCentreIncomingSound('alert', view.id + Date.now());
    }, 2200);
    return () => window.clearInterval(timer);
  }, [view.phase, view.id]);

  useEffect(() => {
    if (view.phase !== 'outgoing') {
      stopRingback();
      return undefined;
    }
    unlockCentreNotifSound();
    startRingback();
    return () => stopRingback();
  }, [view.phase, view.id]);

  useEffect(() => {
    setFull(true);
    setSpeaker(false);
    if (!routeAudio) return undefined;
    setCallAudio(true);
    return () => setCallAudio(false);
  }, [routeAudio, view.id]);

  useEffect(() => {
    if (view.phase === 'incoming') setFull(true);
  }, [view.phase]);

  const toggleSpeaker = () => {
    const next = !speaker;
    setSpeaker(next);
    setCallSpeaker(next);
  };

  const status = view.phase === 'ended'
    ? (view.detail || 'Raccroché')
    : view.phase === 'incoming'
      ? 'Appel entrant'
      : view.phase === 'connecting'
        ? (view.direction === 'out' ? 'Appel…' : 'Connexion…')
        : view.phase === 'outgoing'
          ? 'Sonnerie…'
          : formatClock(elapsed);

  const expanded = full || view.phase === 'incoming';

  return (
    <div className={`centre-call is-${view.phase}${expanded ? ' is-full' : ' is-min'}`}>
      {expanded && view.phase !== 'incoming' && view.phase !== 'ended' && (
        <button type="button" className="centre-call__fold" onClick={() => setFull(false)} aria-label="Réduire">
          <Icon name="chevron-down" size={16} />
        </button>
      )}
      {!expanded && (
        <button type="button" className="centre-call__grow" onClick={() => setFull(true)} aria-label="Agrandir">
          <span className="centre-call__avatar">{initials(view.name)}</span>
          <span>
            <strong>{view.name}</strong>
            <em>{view.held ? 'En attente' : status}</em>
          </span>
        </button>
      )}
      {expanded && (
        <div className="centre-call__body">
          <div className="centre-call__stage">
            <span className="centre-call__ring" />
            <span className="centre-call__avatar">{initials(view.name)}</span>
          </div>
          <strong>{view.name}</strong>
          {phoneLabel && <span className="centre-call__phone">{phoneLabel}</span>}
          {view.provider === 'voip' && <span className="centre-call__via">Tél</span>}
          <em>{view.held ? 'En attente' : status}</em>
        </div>
      )}
      <div className="centre-call__actions">
        {view.phase === 'ended' ? null : view.phase === 'incoming' ? (
          <>
            <button type="button" className="centre-call__ko" onClick={onReject} disabled={busy} aria-label="Refuser">
              <Icon name="phone" size={16} />
            </button>
            <button type="button" className="centre-call__ok" onClick={onAccept} disabled={busy} aria-label="Décrocher">
              <Icon name="phone" size={16} />
            </button>
          </>
        ) : (
          <>
            {expanded && routeAudio && (
              <button
                type="button"
                className={`centre-call__mic${speaker ? ' is-on' : ''}`}
                onClick={toggleSpeaker}
                aria-label={speaker ? 'Écouteur' : 'Haut-parleur'}
              >
                <Icon name={speaker ? 'volume-high' : 'volume'} size={15} />
              </button>
            )}
            {expanded && (
            <button
              type="button"
              className={`centre-call__mic${muted ? ' is-off' : ''}`}
              onClick={onToggleMute}
              disabled={!live}
              aria-label={muted ? 'Micro' : 'Muet'}
            >
              <Icon name={muted ? 'mic-off' : 'mic'} size={15} />
            </button>
            )}
            {expanded && onToggleHold && (
              <button
                type="button"
                className={`centre-call__mic${view.held ? ' is-off' : ''}`}
                onClick={onToggleHold}
                disabled={!live}
                aria-label={view.held ? 'Reprendre' : 'Attente'}
              >
                <Icon name="pause" size={15} />
              </button>
            )}
            <button type="button" className="centre-call__ko" onClick={onHangup} aria-label="Raccrocher">
              <Icon name="phone" size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CentreCallOverlay;
